import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { AutoCompleteComponent } from '@syncfusion/ej2-angular-dropdowns';
import { AccordionComponent, ExpandEventArgs } from '@syncfusion/ej2-angular-navigations';
import { DialogComponent } from '@syncfusion/ej2-angular-popups';
import { closest } from '@syncfusion/ej2-base';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import Utils from '../../shared/helpers/utils';
import { fadeInOut } from '../../shared/helpers/animations';
import { IExportTemplate, ISavedTemplate, IServiceError, DeviceAttribute, Section } from '../../shared/models/index';
import { ExportDataService, StorageManagerService } from '../../shared/services/index';



@Component({
    selector: 'app-attributes-modal',
    templateUrl: './attributes-modal.component.html',
    styleUrls: ['./attributes-modal.component.css'],
    animations: [fadeInOut],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false
})
export class AttributesModalComponent implements OnInit, AfterViewInit, OnDestroy {

  isModal = true;
  animationSettings: Object = { effect: 'Zoom' };
  modalWidth = '50%';
  modalHeight = '80%';
  visible = false;
  position: object = { X: 'center', Y: 'center' };
  mapping = { header: 'section', content: 'attributes' };
  itemsData: any = [];
  fields: Object = { value: 'attributes' };
  autofill = true;
  accordionObj: AccordionComponent;
  actElementObj: AutoCompleteComponent;
  sub: Subscription;
  atcSelected: any;
  resolverData: any[] | IServiceError;
  selectedAttrArray: string[] = [];
  selectAllChecked = false;
  clickEventArgs: Event;
  editedTemplate: any;
  pageLoaded = false;
  templateNameInput: any;
  atcelementInput: any;
  templateName = '';
  template: IExportTemplate;
  savedTemplate: ISavedTemplate = new ISavedTemplate();
  templateNameMissing = false;
  errorMessage: string;
  originalAttributesSet: DeviceAttribute[];
  originalTemplateName = '';

  @Input() attributes: DeviceAttribute[];
  @Input() properties: string[];
  @Input() editTemplateId: number;
  @Input() templates: any[];
  @Output() modified = new EventEmitter<boolean>();

  @ViewChild('dialog')
  public dialog: DialogComponent;

  @ViewChildren(AccordionComponent) accordionQuery: QueryList<ElementRef>;
  @ViewChildren(AutoCompleteComponent) actQuery: QueryList<ElementRef>;

  constructor(private storage: StorageManagerService, private exportDataService: ExportDataService, private toastr: ToastrService) { }

  ngOnInit(): void { }

  onAtcSelect(event) {

    if (event?.isInteracted) {
      if (event?.itemData?.value) {
        // In the event the user tries to search for an attribute under a collapsed accordion item, the code will be unable to scroll to it since it is hidden.
        // To fix this, call expandAll() to force open all the accordion items and then give it 300ms to ensure all are expanded.
        this.expandAll();
        setTimeout(() => {
          // do the search on the fresh data set.
          this.atcSelected = event.itemData?.value;
          this.scrollToElement(`#${this.formatId(this.atcSelected)}`);
        }, 300)

      } else {
        // if clear is clicked scroll to the top
        this.scrollToElement(`#top`);
      }
    }
  }

  getAtcItemData() {

    this.atcelementInput = document.getElementsByName('atcelement')[0];

    if (this.atcelementInput) {
      this.onAtcSelect({ isInteracted: true, itemData: { value: (this.atcelementInput as any).value } });
    }
  }

  formatId(id) {
    return id.replace(/[- )(?]/g, '');
  }

  attrFieldCounter(attr) {

    let counter = attr.fields.length;

    if (attr.sections?.length) {
      attr.sections.forEach(section => {
        counter += section.fields.length;

        // loop deep insided each field in case it has sections
        if (section.sections.length) {
          section.sections.forEach(innerAttr => {
            counter += this.attrFieldCounter(innerAttr);
          });
        }
      });
    }

    return counter;
  }

  getAttrSections(attr) {

    let sections: Section[] = [];

    if (attr.sections.length) {
      sections = attr.sections;

      // loop deep insided each field in case it has sections
      if (sections.length) {
        sections.forEach(innerAttr => {
          sections = sections.concat(this.getAttrSections(innerAttr));
        });
      }
    }

    return sections;
  }
  mergeAttributes(currentAttrs: DeviceAttribute[], savedAttrs: DeviceAttribute[]): DeviceAttribute[] {
    if (!savedAttrs || !savedAttrs.length) {
      // No saved data — return the latest attributes as-is
      return currentAttrs;
    }
    return currentAttrs.map(currentAttr => {
      // Find the matching attribute section in the saved template
      const savedAttr = savedAttrs.find(a => a.caption === currentAttr.caption);
      // Merge fields
      const mergedFields = currentAttr.fields.map(field => {
        const savedField = savedAttr?.fields?.find(f => f.name.toLowerCase() === field.name.toLowerCase());
        return {
          ...field,
          isSelected: savedField ? savedField.isSelected : false
        };
      });
      // Merge nested sections recursively
      const mergedSections = this.mergeSections(currentAttr.sections, savedAttr?.sections || []);
      return { ...currentAttr, fields: mergedFields, sections: mergedSections };
    });
  }
  /** Recursively merge nested sections */
  mergeSections(currentSections: Section[], savedSections: Section[]): Section[] {
    return currentSections.map(currentSec => {
      const savedSec = savedSections.find(s => s.caption === currentSec.caption);

      const mergedFields = currentSec.fields.map(field => {
        const savedField = savedSec?.fields?.find(f => f.name.toLowerCase() === field.name.toLowerCase());
        return {
          ...field,
          isSelected: savedField ? savedField.isSelected : false
        };
      });
      const mergedInnerSections = this.mergeSections(currentSec.sections || [], savedSec?.sections || []);
      return { ...currentSec, fields: mergedFields, sections: mergedInnerSections };
    });
  }
  scrollToElement(target): void {

    const element = document.querySelector(target);

    if (element) {
      this.selectAttribute(element);
      element.scrollIntoView({
        behavior: 'smooth', block: 'center'
      });

      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();

    } else {

      setTimeout(() => {

        const el = document.querySelector(target);

        if (el) {
          this.selectAttribute(el);
          el.scrollIntoView({
            behavior: 'smooth', block: 'center'
          });
        }
      }, 300);
    }
  }

  selectAttribute(element) {

    this.clearAttributesActive();

    if (element && element.id !== 'top') {
      element.classList.add('active');
    }
  }

  clearAttributesActive() {

    const items = document.getElementsByClassName('accordionItem');

    if (items && items.length) {
      const arr = Array.from(items);

      const activeItem = arr.find(i => i.classList.contains('active'));
      if (activeItem) {
        activeItem.classList.remove('active');
      }
    }
  }

  defaultSelectedAttrArray() {

    const defaultSection = 'Device Key Identification';
    const defaultFields = ['UDI_DEVICE_IDENTIFIER', 'UDI_DEVICE_IDENTIFIER_ISSUING_AGENCY', 'DATA_SCOPE'];
    //const defaultFields = ['Primary DI/EUDAMED ID', 'Primary DI Issuing Agency/EUDAMED ID Issuing Agency', 'Data Scope'];
    defaultFields.forEach(attr => {
      if (!this.selectedAttrArray.includes(attr)) {
        this.selectedAttrArray.push(attr);
      }
    });

    const section = this.attributes.find(f => f.caption.toLowerCase() === defaultSection.toLowerCase());

    if (section && section.fields && section.fields.length) {
      section.fields.forEach(field => {

        if (field.caption.toLowerCase() === defaultFields[0].toLowerCase() || field.caption.toLowerCase() === defaultFields[1].toLowerCase() || field.caption.toLowerCase() === defaultFields[2].toLowerCase()) {
          field.isSelected = true;
        }
      });
    }
  }

  makeArrayUnique(arr): any[] {

    let uniqueArray = [];

    if (arr && arr.length) {

      uniqueArray = arr.map(item => item)
        .filter((value, index, self) => self.indexOf(value) === index);
    }

    return uniqueArray;
  }

  selectAll(event) {

    if (event.checked) {

      this.selectAllChecked = true;

      this.selectedAttrArray = [];

      // insert default fields - Primary DI, Primary DI Issuing agency and Data Scope
      this.defaultSelectedAttrArray();
      this.selectedAttrArray = this.selectedAttrArray.concat(this.properties);

      // make the array unique
      this.selectedAttrArray = this.makeArrayUnique(this.selectedAttrArray);

    } else {
      this.selectAllChecked = false;
      this.selectedAttrArray = [];

      // insert default fields - Primary DI, Primary DI Issuing agency and Data Scope
      this.defaultSelectedAttrArray();
    }

    this.updateSavedTemplate('all', event.checked);
  }

  updateSavedTemplate(item, isSelected) {

    if (this.savedTemplate) {
      if (this.savedTemplate.exportDataTemplate.deviceAttributes && this.savedTemplate.exportDataTemplate.deviceAttributes.length) {

        // if Select All is checked/unchecked
        if (item.toLowerCase() === 'all') {

          this.savedTemplate.exportDataTemplate.deviceAttributes.forEach(da => {

            // fields on the attribute level.
            da.fields.forEach(field => {
              field.isSelected = isSelected;
            });

            // update inner section fields
            let sections: Section[] = [];

            sections = this.getAttrSections(da);

            sections.forEach(innerSection => {
              innerSection.fields.forEach(field => {
                field.isSelected = isSelected;
              });
            });
          });
        } else {

          let field;

          // if a single item checked/unchecked
          this.savedTemplate.exportDataTemplate.deviceAttributes.forEach(da => {

            field = da.fields.find(f => f.name.toLowerCase() === item.toLowerCase());

            if (field) {
              field.isSelected = isSelected;
            } else {
              // try to search in the nested sections
              let sections: Section[] = [];

              sections = this.getAttrSections(da);

              if (sections.length) {
                sections.forEach(section => {
                  field = section.fields.find(f => f.name.toLowerCase() === item.toLowerCase());

                  if (field) {
                    field.isSelected = isSelected;
                  }
                });
              }
            }
          });
        }
      }
    }
  }

  chkChanged(event, item) {

    this.clearAttributesActive();

    if (event.checked) {

      if (!this.selectedAttrArray.includes(item)) {
        this.selectedAttrArray.push(item);
      }
    } else {

      if (this.selectedAttrArray.includes(item)) {

        const index = this.selectedAttrArray.findIndex(i => i === item);
        this.selectedAttrArray.splice(index, 1);
      }
    }

    this.updateSavedTemplate(item, event.checked);
  }

  checkSectionChanged(event, section) {

    let fields = [];
    let sections: Section[] = [];

    const attr = this.attributes.find(a => a.caption.toLowerCase() === section.toLowerCase());

    sections = this.getAttrSections(attr);

    // attribute level fields
    if (attr) {
      fields = attr.fields;
    }

    // attribute sections level fields
    if (sections && sections.length) {
      sections.forEach(innerSection => {
        fields = fields.concat(innerSection.fields);
      });
    }

    if (fields && fields.length) {

      if (event.checked) {
        // insert all attributes from the section are in the checked array.
        fields.forEach(item => {
          if (!this.selectedAttrArray.includes(item.name)) {
            this.selectedAttrArray.push(item.name);
            this.updateSavedTemplate(item.name, true);
          }
        });
      } else {

        // remove all attributes from the section are in the checked array.
        fields.forEach(item => {
          if (this.selectedAttrArray.includes(item.name)) {

            const index = this.selectedAttrArray.findIndex(i => i.toLowerCase() === item.name.toLowerCase());
            this.selectedAttrArray.splice(index, 1);

            this.updateSavedTemplate(item.name, false);
          }
        });

        // insert default fields - Primary DI, Primary DI Issuing agency, and Data Scope
        this.defaultSelectedAttrArray();
      }
    }
  }

  isHeaderDisabled(section) {

    if (section === 'DEVICE KEY IDENTIFICATION') {
      return true;
    }

    return false;
  }

  isHeaderChecked(section) {

    let fields = [];
    let count = 0;

    const attrSection = this.attributes.find(a => a.caption.toLowerCase() === section.toLowerCase());

    if (attrSection) {
      fields = attrSection.fields;

      // attribute sections level fields
      if (attrSection.sections && attrSection.sections.length) {
        attrSection.sections.forEach(innerSection => {
          fields = fields.concat(innerSection.fields);
        });
      }

      if (fields && fields.length) {

        // check if all attributes from the section are in the checked array.
        fields.forEach(element => {
          if (this.selectedAttrArray.includes(element.name)) {
            count++;
          }
        });
      }
    } else {
      return false;
    }

    return fields.length === count;
  }

  expanding(e: ExpandEventArgs) {

    if (this.clickEventArgs) {

      const header = closest(this.clickEventArgs.target as HTMLElement, '.e-acrdn-header');
      const checkboxEle = closest(this.clickEventArgs.target as HTMLInputElement, 'label');
      if (header && checkboxEle) {
        e.cancel = true;
        this.clickEventArgs = undefined;
        return;
      }
    }
  }

  public onClick(e) {
    this.clickEventArgs = e.originalEvent;
  }

  isChecked(item): boolean {

    let retVal = false;

    if (this.selectedAttrArray && this.selectedAttrArray.length) {
      retVal = this.selectedAttrArray.includes(item);
    }

    return retVal;
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  ngAfterViewInit(): void {

    this.sub = this.accordionQuery.changes.subscribe((comps: QueryList<AccordionComponent>) => {
      this.accordionObj = comps.first;
    });

    this.sub = this.actQuery.changes.subscribe((comps: QueryList<AutoCompleteComponent>) => {
      this.actElementObj = comps.first;
    });
  }

  isDisabled(item) {
    if (item === 'UDI_DEVICE_IDENTIFIER' || item === 'UDI_DEVICE_IDENTIFIER_ISSUING_AGENCY' || item === 'DATA_SCOPE') {
      return true;
    }

    return false;
  }

  onDialogOpen(event) {

    let attrSections: Section[] = [];

    // this flag is to delay opening a modal until the checkboxes are set.
    this.pageLoaded = false;
    this.templateNameInput = document.getElementById('templateName');

    // Prepare the empty template for future saving.
    this.savedTemplate.exportDataTemplate = new IExportTemplate();

    // insert default fields - Primary DI, Primary DI Issuing agency and Data Scope
    this.defaultSelectedAttrArray();

    // if the Input "templateNameEdit" is not null - means we are in the Edit mode - insert all attribues into the selected array. It will check the checkboxes and prepares for saving.

    // this is "edit" action find the attributes of this templates so only the edited template is displayed
    if (this.editTemplateId) {

      this.editedTemplate = this.templates.find(a => a.exportTemplateId === this.editTemplateId);

      if (this.editedTemplate) {

        // store the original name if case edit is cancelled
        this.originalTemplateName = this.editedTemplate.exportTemplateName;
        // store a copy of the original attributes in case the edis is cancelled.
        this.originalAttributesSet = Utils.deepCopy(this.editedTemplate.exportTemplate.deviceAttributes);

        if (this.templateNameInput) {
          this.templateNameInput.value = this.editedTemplate.exportTemplateName;
        }

        if (this.editedTemplate.exportTemplate?.deviceAttributes && this.editedTemplate.exportTemplate?.deviceAttributes?.length) {
          this.savedTemplate.exportDataTemplate.deviceAttributes =
            this.mergeAttributes(this.attributes, this.editedTemplate.exportTemplate?.deviceAttributes);
          this.savedTemplate.exportDataTemplateId = this.editTemplateId;

          this.editedTemplate.exportTemplate?.deviceAttributes.forEach(attr => {

            // inside there is an array of attributes called "fields"
            if (attr.fields && attr.fields.length) {

              const selectedAttributes = attr.fields.filter(a => a.isSelected);

              attrSections = this.getAttrSections(attr);

              // get selected fields from the inner level
              if (attrSections && attrSections.length) {

                attrSections.forEach(innerSection => {

                  if (innerSection.fields && innerSection.fields.length) {
                    innerSection.fields.forEach(innerField => {
                      if (innerField.isSelected && !this.selectedAttrArray.includes(innerField.name)) {
                        this.selectedAttrArray.push(innerField.name);
                      }
                    });
                  }
                });
              }

              if (selectedAttributes && selectedAttributes.length) {
                selectedAttributes.forEach(field => {

                  this.updateSavedTemplate(field.name, field.isSelected);

                  if (!this.selectedAttrArray.includes(field.name)) {
                    this.selectedAttrArray.push(field.name);
                  }
                });
              }
            }
          });

          if (this.properties.length) {
            this.properties = this.properties.sort();
          }
        }
      }
    } else {
      this.savedTemplate.exportDataTemplate.deviceAttributes = this.attributes;
    }

    this.pageLoaded = true;
  }

  onDialogClose(event) {
    this.selectedAttrArray = [];
    this.pageLoaded = false;
    this.originalTemplateName = '';
    this.originalAttributesSet = [];
    this.templateNameMissing = false;

    if (this.templateNameInput) {
      this.templateNameInput.value = '';
    }
  }

  modalCancel() {

    this.templateNameInput = document.getElementById('templateName');

    // use the saved deviceAttributes array to reverse to the original state
    if (this.editTemplateId) {
      this.editedTemplate.exportTemplate.deviceAttributes = this.originalAttributesSet;

      if (this.templateNameInput) {
        this.templateNameInput.value = this.originalTemplateName;
      }
    } else {
      if (this.templateNameInput) {
        this.templateNameInput.value = '';
      }
    }

    if (this.actElementObj) {
      this.actElementObj.value = '';
    }

    this.dialog.hide();
  }

  templateNameKeyUp(event) {

    if (event.target.value) {
      this.templateNameMissing = false;
    } else {
      this.templateNameMissing = true;
    }
  }

  checkDefaultAttributes() {

    const defaultSection = 'Device Key Identification';
    const defaultFields = ['UDI_DEVICE_IDENTIFIER', 'UDI_DEVICE_IDENTIFIER_ISSUING_AGENCY', 'DATA_SCOPE'];

    if (this.savedTemplate) {
      if (this.savedTemplate.exportDataTemplate.deviceAttributes && this.savedTemplate.exportDataTemplate.deviceAttributes.length) {

        defaultFields.forEach(attr => {

          const section = this.savedTemplate.exportDataTemplate.deviceAttributes.find(s => s.caption.toLowerCase() === defaultSection.toLowerCase());

          if (section) {
            const field = section.fields.find(da => da.name.toLowerCase() === attr.toLowerCase());

            if (field) {
              field.isSelected = true;
            }
          }
        });
      }
    }
  }


  saveTemplate() {

    if (this.templateNameInput) {
      this.templateName = this.templateNameInput.value;
    }

    if (!this.templateName) {
      this.templateNameMissing = true;

      // scroll to the top to display the "required" message
      this.scrollToElement(`#top`);
    } else {

      // The object should contain the template name and unique array of attributes.
      if (this.selectedAttrArray && this.selectedAttrArray.length) {

        if (this.savedTemplate) {
          this.savedTemplate.exportDataTemplateName = this.templateName;
          this.savedTemplate.exportDataTemplateId = this.editTemplateId;

          this.checkDefaultAttributes();

          this.sub = this.exportDataService.saveExportTemplate(JSON.parse(JSON.stringify(this.savedTemplate))).subscribe(response => {

            if (response['isSuccess']) {
              this.modalCancel();
              this.modified.emit(true);
              this.toastr.success(`The template saved successfully.`);
            } else {
              this.errorMessage = response && response['message'] ? response['message'] : 'Template save failed.';
              this.toastr.error(this.errorMessage);
            }
          });
        }
      }
    }
  }

  isSelectAllChecked() {
    return this.selectedAttrArray?.length === this.properties?.length;
  }

  accordionCreated(event) {
    this.expandAll();
    this.accordionObj.refresh();
  }

  collapseAll() {
    this.accordionObj.expandItem(false);
  }

  expandAll() {
    this.accordionObj.expandItem(true);
  }
}
